import { useDispatch } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import Router from "./routes/routes";
import { setIsDesktop, setIsMobile } from "store/deviceSlice";
import { fetchCities } from "store/citiesSlice";
import { useEffect, useState, lazy, Suspense, useCallback } from "react";
import { FormProvider } from "contexts/FormContext";
import { CompareProvider } from "contexts/CompareContext";
import { WishlistProvider } from "contexts/WishlistContext";
import {
  TooltipManagerProvider,
  TooltipProvider,
} from "components/tooltip/Tooltip";

// Native matchMedia hook - replaces react-responsive
const useMediaQuery = (query) => {
  const getMatches = useCallback(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia(query).matches;
    }
    return false;
  }, [query]);

  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);

    setMatches(mediaQuery.matches);
    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [query, getMatches]);

  return matches;
};

const Toaster = lazy(() =>
  import("react-hot-toast").then((m) => ({ default: m.Toaster }))
);
const WeChatFloatingButton = lazy(() =>
  import("components/WeChatFloatingButton/WeChatFloatingButton")
);
const CompareStrip = lazy(() => import("components/common/CompareStrip"));
const CookiesPopup = lazy(() => import("components/cookiesPopup/CookiesPopup"));

function App() {
  const dispatch = useDispatch();
  const [showCookiesPopup, setShowCookiesPopup] = useState(false);
  const isMobile = useMediaQuery("(max-width: 769px)");
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  useEffect(() => {
    dispatch(setIsMobile(isMobile));
    dispatch(setIsDesktop(isDesktop));
  }, [isMobile, isDesktop, dispatch]);

  useEffect(() => {
    const loadCities = () => dispatch(fetchCities());

    if ("requestIdleCallback" in window) {
      const idleId = requestIdleCallback(loadCities, { timeout: 3000 });
      return () => cancelIdleCallback(idleId);
    } else {
      const timerId = setTimeout(loadCities, 3000);
      return () => clearTimeout(timerId);
    }
  }, [dispatch]);

  useEffect(() => {
    const cookiesAccepted = localStorage.getItem("cookiesAccepted");
    const cookiesDeclined = localStorage.getItem("cookiesDeclined");

    if (!cookiesAccepted && !cookiesDeclined) {
      const timer = setTimeout(() => {
        setShowCookiesPopup(true);
        document.body.style.overflow = "hidden";
      }, 5000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, []);

  useEffect(() => {
    let location = window.location.pathname;
    if (showCookiesPopup && !location.includes("cookie-policy")) {
      document.body.style.setProperty("overflow", "hidden", "important");
      document.documentElement.style.setProperty(
        "overflow",
        "hidden",
        "important"
      );

      const scrollContainers = document.querySelectorAll(
        ".snap-y, .snap-mandatory, .overflow-y-scroll"
      );
      scrollContainers.forEach((container) => {
        container.style.setProperty("overflow", "hidden", "important");
        container.style.setProperty("scroll-snap-type", "none", "important");
      });
    } else {
      document.body.style.removeProperty("overflow");
      document.documentElement.style.removeProperty("overflow");

      const scrollContainers = document.querySelectorAll(
        ".snap-y, .snap-mandatory, .overflow-y-scroll"
      );
      scrollContainers.forEach((container) => {
        container.style.removeProperty("overflow");
        container.style.removeProperty("scroll-snap-type");
      });
    }
  }, [showCookiesPopup]);

  const handleAcceptCookies = (e) => {
    e.preventDefault();
    localStorage.setItem("cookiesAccepted", "true");
    setShowCookiesPopup(false);
  };

  const handleDeclineCookies = (e) => {
    e.preventDefault();
    localStorage.setItem("cookiesDeclined", "true");
    setShowCookiesPopup(false);
  };

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isHomepage = currentPath === "/";

    let lastHeight = window.visualViewport?.height || window.innerHeight;

    const setViewportHeight = () => {
      const newHeight = window.visualViewport?.height || window.innerHeight;

      if (isHomepage) {
        // Homepage logic - prevent white space on keyboard open
        const decreaseAmount = lastHeight - newHeight;

        if (newHeight >= lastHeight) {
          // Height increased - always update
          lastHeight = newHeight;
          const vh = newHeight * 0.01;
          document.documentElement.style.setProperty("--vh", `${vh}px`);
        } else if (decreaseAmount > 150) {
          // Significant decrease - likely keyboard, keep lastHeight
          const vh = lastHeight * 0.01;
          document.documentElement.style.setProperty("--vh", `${vh}px`);
        } else {
          // Small decrease - update normally
          lastHeight = newHeight;
          const vh = newHeight * 0.01;
          document.documentElement.style.setProperty("--vh", `${vh}px`);
        }
      } else {
        // Other pages - use original simple logic
        if (newHeight >= lastHeight || newHeight > 400) {
          lastHeight = newHeight;
          const vh = newHeight * 0.01;
          document.documentElement.style.setProperty("--vh", `${vh}px`);
        } else {
          const vh = lastHeight * 0.01;
          document.documentElement.style.setProperty("--vh", `${vh}px`);
        }
      }
    };

    setViewportHeight();
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", setViewportHeight);
    } else {
      window.addEventListener("resize", setViewportHeight);
      window.addEventListener("orientationchange", setViewportHeight);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", setViewportHeight);
      } else {
        window.removeEventListener("resize", setViewportHeight);
        window.removeEventListener("orientationchange", setViewportHeight);
      }
    };
  }, []);

  return (
    <BrowserRouter>
      <TooltipManagerProvider>
        <TooltipProvider>
          <FormProvider>
            <CompareProvider>
              <WishlistProvider>
                <Suspense fallback={null}>
                  <Router />
                </Suspense>
                <Suspense fallback={null}>
                  <CompareStrip />
                  <WeChatFloatingButton />
                </Suspense>
                {showCookiesPopup && (
                  <Suspense fallback={null}>
                    <div className="fixed inset-0 z-[999999999998] bg-black flex items-end justify-start p-2 lg:p-4">
                      <div className="z-[999999999999] animate-slide-up">
                        <CookiesPopup
                          onAccept={handleAcceptCookies}
                          onDecline={handleDeclineCookies}
                        />
                      </div>
                    </div>
                  </Suspense>
                )}
              </WishlistProvider>
            </CompareProvider>
          </FormProvider>
          <Suspense fallback={null}>
            <Toaster position="top-right" reverseOrder={false} />
          </Suspense>
        </TooltipProvider>
      </TooltipManagerProvider>
    </BrowserRouter>
  );
}

export default App;
